import { useCallback, useMemo } from 'react'
import { LinkButton } from '@/Reservations/Search/ExperienceCard/LinkButton/LinkButton'
import type { OfferType } from '@sevenrooms/domain'
import { ReservationWidget } from '@sevenrooms/domain/constants'
import { useLocales } from '@sevenrooms/locales'
import { DateOnly } from '@sevenrooms/timepiece'
import { VStack, Flex } from '@sevenrooms/ui-kit/layout'
import { Text } from '@sevenrooms/ui-kit/typography'
import { RequestOrPriorityAlertButton } from '../../../../components/RequestOrPriorityAlertButton'
import { getRelativeFormattedDate } from '../../../../components/ReservationDayPickerForm/getRelativeFormattedDate'
import {
  type AvailabilityTimeWithUpSellCost,
  useExperienceValidDates,
  useReservationsRoute,
  useSingleDayAvailability,
  useWidgetLanguage,
  useWidgetSettings,
} from '../../../../hooks'
import { reservationWidgetMessages } from '../../../../reservationWidgetMessages'
import { TimeSlots } from '../TimeSlots/TimeSlots'
import { TimeSlotsSkeleton } from '../TimeSlots/TimeSlotsSkeleton'
import type { ExperienceVenueInfo } from '../index'

interface NextAvailableDayProps {
  experienceUrlKey: string
  offerType: OfferType
  clientId?: string
  partySize: number
  startDate?: string
  haloTimeIntervalMinutes?: number
  startTime?: string
  onTimeSlotClick?: (slot: AvailabilityTimeWithUpSellCost) => void
  timeSlotsDisplayCount?: number
  onRequestBtnClick?: (requestDate: string, requestPartySize: number) => void
  onPriorityAlertBtnClick?: (requestDate: string, requestPartySize: number) => void
  onDateClick?: (date: string) => void
  descriptionType?: 'short' | 'full'
  venueInfo: ExperienceVenueInfo
}

export function NextAvailableDay({
  experienceUrlKey,
  offerType,
  onTimeSlotClick,
  onRequestBtnClick,
  onPriorityAlertBtnClick,
  onDateClick,
  partySize,
  startDate,
  startTime,
  haloTimeIntervalMinutes,
  clientId,
  timeSlotsDisplayCount,
  descriptionType,
  venueInfo,
}: NextAvailableDayProps) {
  const { formatMessage } = useLocales()
  const { selectedLanguage } = useWidgetLanguage()
  const { id: venueId, urlKey: venueUrlKey, venueToday, startOfDayTime, currencyCode, availabilityCacheDays } = venueInfo
  const { defaultSearchTime } = useWidgetSettings()
  const { channel } = useReservationsRoute()
  const isPrivateEventsExperience = offerType === 'PDR'

  const {
    data: { firstAvailableDayForBooking },
    isLoading: isLoadingValidDates,
    isFetching: isFetchingValidDates,
    isError: isErrorValidDates,
  } = useExperienceValidDates({
    partySize,
    selectedDate: startDate,
    experienceUrlKey,
    venueUrlKey,
    venueToday,
    availabilityCacheDays,
    skipRequests: !partySize,
  })

  const {
    isFetching: isAvailabilityFetching,
    errorCode: errorCodeFetchingAvailability,
    data: nextAvailableDayAvailabilityData,
  } = useSingleDayAvailability({
    venueId,
    venue: venueUrlKey,
    startDate: firstAvailableDayForBooking as string,
    partySize,
    startTime: startTime || ReservationWidget.AllTimesOption,
    selectedLangCode: selectedLanguage,
    haloTimeIntervalMinutes:
      haloTimeIntervalMinutes || (ReservationWidget.HaloMinutesOptions[ReservationWidget.HaloMinutesOptions.length - 1] as number),
    channel: channel || ReservationWidget.SevenRoomsWidgetChannel,
    haloSizeInterval: ReservationWidget.WholeDayHaloSizeInterval,
    timeSlot: defaultSearchTime,
    startOfDayTime,
    skipRequests: isLoadingValidDates || !firstAvailableDayForBooking,
    clientId,
    experienceId: experienceUrlKey,
  })

  const onDateLabelClick = useCallback(() => {
    if (firstAvailableDayForBooking) {
      onDateClick?.(firstAvailableDayForBooking)
    }
  }, [onDateClick, firstAvailableDayForBooking])

  const requestOrPriorityAlertAvailability = useMemo(() => {
    const requestTimeRange = nextAvailableDayAvailabilityData?.requestTimeRange
    const reservationTimesInHalo = nextAvailableDayAvailabilityData?.reservationTimesInHalo

    if (requestTimeRange?.length && partySize && firstAvailableDayForBooking) {
      return (
        <RequestOrPriorityAlertButton
          isPrivateEvent={isPrivateEventsExperience}
          buttonVariant={reservationTimesInHalo?.length === 0 ? 'primary' : 'secondary'}
          onRequestButtonClick={() => onRequestBtnClick?.(firstAvailableDayForBooking, partySize)}
          onPriorityAlertButtonClick={() => onPriorityAlertBtnClick?.(firstAvailableDayForBooking, partySize)}
          partySize={partySize}
        />
      )
    }
    return null
  }, [
    firstAvailableDayForBooking,
    isPrivateEventsExperience,
    nextAvailableDayAvailabilityData?.requestTimeRange,
    nextAvailableDayAvailabilityData?.reservationTimesInHalo,
    onPriorityAlertBtnClick,
    onRequestBtnClick,
    partySize,
  ])

  if (isErrorValidDates) {
    // This should not happen. If so, there's a bug on the BE.
    // eslint-disable-next-line no-console
    console.error('An error during fetching next available dates.')
    return null
  }

  if (errorCodeFetchingAvailability) {
    // eslint-disable-next-line no-console
    console.error(
      `An error ${errorCodeFetchingAvailability} during fetching availability for next available date ${firstAvailableDayForBooking}`
    )
    return null
  }

  if (isLoadingValidDates || (isFetchingValidDates && !firstAvailableDayForBooking)) {
    return <TimeSlotsSkeleton showHeader />
  }

  if (firstAvailableDayForBooking) {
    const timeSlots = nextAvailableDayAvailabilityData?.reservationTimesInHalo
    if (!timeSlots?.length && !isAvailabilityFetching) {
      return null
    }
    const startDateJs = DateOnly.fromSafe(firstAvailableDayForBooking)?.toJsDate()
    const formattedStartDate = startDateJs ? getRelativeFormattedDate({ date: startDateJs }) : undefined
    return (
      <VStack spacing="sm">
        <Text textStyle="body1Bold">{formatMessage(reservationWidgetMessages.resWidgetNextAvailableDateLabel)}</Text>
        <VStack spacing="m">
          {isAvailabilityFetching ? (
            <>
              {formattedStartDate && (
                <Flex>
                  <LinkButton text={formattedStartDate} onClick={onDateLabelClick} data-test="sr-room-card-date-label-button" />
                </Flex>
              )}
              <TimeSlotsSkeleton />
            </>
          ) : (
            <TimeSlots
              timeSlotVariant="secondary"
              timeSlotsDisplayCount={timeSlotsDisplayCount}
              timeSlots={timeSlots}
              startDate={formattedStartDate}
              currencyCode={currencyCode}
              onTimeSlotClick={onTimeSlotClick}
              onDateClick={onDateLabelClick}
              requestOrPriorityAlertAvailability={requestOrPriorityAlertAvailability}
              descriptionType={descriptionType}
            />
          )}
        </VStack>
      </VStack>
    )
  }

  return null
}
